import { getConditionLabel } from "@features/ax/utils";
import { DynamicForm } from "@shared/components/DynamicForm";
import { GenericTable } from "@shared/components/GenericTable";
import { PageHeader } from "@shared/components/PageHeader";
import { useCurrency } from "@shared/selectors/globalSelectors";
import {
  FormConfig,
  FormField,
  FormService,
} from "@shared/services/FormServices";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Checkbox } from "@shared/ui/checkbox";
import { toast } from "@shared/ui/use-toast";
import { FIELD_CLASSES, PAGE_SIZE } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { ArrowLeft } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useCreateSchedule,
  useGetAllAssets,
  useGetAllFacilities,
  useGetAllNetworks,
  useGetAllSOPs,
  useGetAllSystems,
  useGetChoices,
  useGetScheduleById,
  useGetSOPsForEventRules,
  useUpdateSchedule,
} from "../../../hooks";
import { EntityFilterPanel } from "./filters/EntityFilterPanel";
import { SOPFilterPanel } from "./filters/SOPFilterPanel";

// Types
interface SOP {
  id: string;
  name: string;
  serviceAssociation: string;
  operationType: string;
  tasks: string[];
  totalCost: string;
}

interface Entity {
  id: string;
  name: string;
  type: string;
  location?: string;
  facility?: string;
  system?: string;
  conditionScore?: number;
  status?: string;
  criticality?: string;
  zone?: string;
  systems?: number;
  assets?: number;
  designCapacity?: string;
  operatingStatus?: string;
  currentCapacity?: string;
  capacity?: string;
  customerConnections?: number;
  zones?: string[];
}
const AddSchedule: React.FC = () => {
  const { id, action } = useParams<{ id: string; action: string }>();
  const currency = useCurrency();
  const navigate = useNavigate();
  const remoteUtilityId = getRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();
  const isEditMode = action == "edit";
  const [selectedEventSOP, setSelectedEventSOP] = useState<number>();

  // State management
  const [formData, setFormData] = useState({
    scheduleName: "",
    status: "",
    scheduleType: "",
    taskDescription: "",
    priority: "",
    estimatedDuration: "",
    frequencyType: "",
    recurrence: "",
    startDate: "",
    endDate: "",
    autoGenerate: false,
    generateDays: "",
    dynamicSchedule: false,
    timeOfDay: "",
    weeklyDays: [],
    monthlyType: "specific_date",
    dateOfMonth: "",
    monthlyWeek: "",
    monthlyDay: "",
    quarterlyMonth: "",
    quarterlyDay: "",
    annualMonth: "",
    annualDay: "",
    usageType: "",
    usageInterval: "",
    eventRules: [],
  });

  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [selectedSOP, setSelectedSOP] = useState<number>();
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Pagination parameters - using URL search params with proper defaults
  const pageSize = PAGE_SIZE;
  const currentPage = parseInt(searchParams.get("page") || "1");
  const sopCurrentPage = parseInt(searchParams.get("sopPage") || "1");
  const entityCurrentPage = parseInt(searchParams.get("entityPage") || "1");

  const mapSOPServiceAssociation = (uiValue: string) => {
    const serviceAssociationMap: Record<string, string> = {
      assets: "2",
      systems: "4",
      facilities: "3",
      networks: "5",
    };
    return serviceAssociationMap[uiValue] || "";
  };

  // Pagination state management
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  const [sopPagination, setSopPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  const [entityPagination, setEntityPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Memoized query parameters
  const queryParams = useMemo(
    () => ({
      page: currentPage,
      search: searchParams.get("search") || "",
      meterType: searchParams.get("meterType") || "",
      status: searchParams.get("status") || "",
      startDate: searchParams.get("startDate") || "",
      endDate: searchParams.get("endDate") || "",
    }),
    [currentPage, searchParams]
  );

  const sopQueryParams = useMemo(
    () => ({
      page: sopCurrentPage,
      limit: pageSize,
      serviceAssociation: searchParams.get("serviceAssociation") || "",
      operationType: searchParams.get("operationType") || "",
      search_data: searchParams.get("sopQuery") || "",
    }),
    [sopCurrentPage, pageSize, searchParams]
  );

  const entityQueryParams = useMemo(
    () => ({
      page: entityCurrentPage,
      limit: pageSize,
      area: searchParams.get("area") || "",
      type: searchParams.get("type") || "",
      search_data: searchParams.get("entityQuery") || "",
      system: searchParams.get("system") || "",
      facility: searchParams.get("facility") || "",
      network: searchParams.get("network") || "",
    }),
    [entityCurrentPage, pageSize, searchParams]
  );

  // Choices data
  const { data: scheduleTypeData } = useGetChoices("schedule_type");
  const { data: priorityData } = useGetChoices("default_priority");
  const { data: frequencyTypeData } = useGetChoices("frequency_type");
  const { data: recurrenceData } = useGetChoices("time_recurrence");
  const { data: usageTypeData } = useGetChoices("usage_type");

  // Only call useGetScheduleById if action is edit and id exists
  const { data: scheduleData } = useGetScheduleById(id);

  // API calls with proper pagination
  const { data: sopData } = useGetAllSOPs(
    sopQueryParams.search_data,
    mapSOPServiceAssociation(sopQueryParams.serviceAssociation),
    sopQueryParams.operationType,
    sopQueryParams.page,
    sopQueryParams.limit
  );
  const { data: eventSOPData } = useGetSOPsForEventRules(
    selectedSOP
      ? sopData?.results?.find((sop: any) => sop.id === selectedSOP)
          ?.sopAssociationType
      : undefined
  );
  const serviceAssociation = sopData?.results?.find(
    (sop: any) => sop.id === selectedSOP
  )?.sopAssociationTypeDisplay;

  const { data: assetData } = useGetAllAssets(
    currentPage,
    pageSize,
    serviceAssociation,
    entityQueryParams.search_data,
    entityQueryParams.type,
    entityQueryParams.area,
    entityQueryParams.system,
    entityQueryParams.network,
    entityQueryParams.facility
  );
  const { data: systemData } = useGetAllSystems(
    serviceAssociation,
    entityQueryParams.search_data,
    entityQueryParams.type,
    entityQueryParams.area,
    entityQueryParams.facility
  );
  const { data: facilityData } = useGetAllFacilities(
    currentPage,
    pageSize,
    serviceAssociation,
    entityQueryParams.search_data,
    entityQueryParams.type,
    entityQueryParams.area
  );

  const { data: networkData } = useGetAllNetworks(
    currentPage,
    pageSize,
    serviceAssociation,
    entityQueryParams.search_data,
    entityQueryParams.type,
    entityQueryParams.facility
  );
  const createSchedule = useCreateSchedule(currentPage, pageSize);
  const updateSchedule = useUpdateSchedule(currentPage, pageSize);
  const mapScheduleDataToForm = (apiData) => {
    const eventConfig = apiData.additionalData?.eventConfig;

    let eventRules: any[] = [];

    if (eventConfig) {
      // Handle CALCULATED_ASSET_VALUE trigger source
      if (eventConfig.CALCULATED_ASSET_VALUE) {
        const calcConfig = eventConfig.CALCULATED_ASSET_VALUE;
        eventRules.push(
          ...(calcConfig.conditions || []).map((cond) => ({
            triggerSource: "calculated",
            metric: cond.metric?.toLowerCase().replace("_", "-"),
            operator:
              cond.operator === "lt"
                ? "drop-below"
                : cond.operator === "gt"
                ? "rises-above"
                : "is-equal-to",
            value: cond.threshold,
          }))
        );
      }

      // Handle SERVICE_ORDERS trigger source
      if (eventConfig.SERVICE_ORDERS) {
        const serviceConfig = eventConfig.SERVICE_ORDERS;
        eventRules.push(
          ...(serviceConfig.conditions || []).map((cond) => ({
            triggerSource: "service-order",
            sops: parseInt(cond.sopId),
            readingType: cond.reading,
            operator:
              cond.operator === "lt"
                ? "drop-below"
                : cond.operator === "gt"
                ? "rises-above"
                : "is-equal-to",
            value: cond.threshold,
          }))
        );
      }
    }

    return {
      ...apiData,
      eventRules,
    };
  };

  // Auto-populate form data when scheduleData changes (for edit mode)
  useEffect(() => {
    if (isEditMode && scheduleData && !isDataLoaded) {
      const data = scheduleData;
      const mappedData = mapScheduleDataToForm(data);
      const newFormData = {
        scheduleName: data?.scheduleName || "",
        status: data?.status?.toString() || "",
        scheduleType: data?.scheduleType?.toString() || "",
        taskDescription: data?.taskDescription || "",
        priority: data?.defaultPriority?.toString() || "",
        estimatedDuration: data?.estimatedDuration?.toString() || "",
        frequencyType: data?.frequencyType?.toString() || "",
        recurrence:
          data?.additionalData?.frequencyConfig?.timeRecurrence?.toString() ||
          "",
        startDate: data?.additionalData?.frequencyConfig?.startDate || "",
        endDate: data?.additionalData?.frequencyConfig?.endDate || "",
        autoGenerate: data?.autoGenerateServiceOrders || false,
        generateDays: data?.generateDaysBefore?.toString() || "",
        dynamicSchedule: data?.dynamicAssetInclusion || false,
        timeOfDay: data?.additionalData?.frequencyConfig?.preferredTime,
        weeklyDays: data?.additionalData?.frequencyConfig?.daysOfWeek || [],
        monthlyType:
          data?.additionalData?.frequencyConfig?.monthlyScheduleType ||
          "specific_date",
        dateOfMonth: data?.additionalData?.frequencyConfig?.dayOfMonth || "",
        monthlyWeek: data?.additionalData?.frequencyConfig?.monthlyWeek || "",
        monthlyDay: data?.additionalData?.frequencyConfig?.monthlyDay || "",
        quarterlyMonth: data?.quarterlyMonth?.toString() || "",
        quarterlyDay: data?.quarterlyDay?.toString() || "",
        annualMonth: data?.annualMonth?.toString() || "",
        annualDay: data?.annualDay?.toString() || "",
        usageType:
          data?.additionalData?.valueConfig?.usageType?.toString() || "",
        usageInterval:
          data?.additionalData?.valueConfig?.usageInterval?.toString() || "",
        eventRules: mappedData?.eventRules || [],
      };

      setFormData(newFormData);
      FormService.populateForm(form, newFormData, fields);

      setTimeout(() => {
        const timeValue = data?.additionalData?.frequencyConfig?.preferredTime;
        if (timeValue) {
          // Set the time field directly with the string value
          form.setValue("timeOfDay", timeValue);
        }
      }, 100);

      const sopId = parseInt(data?.sopId);
      if (sopId) {
        setSelectedSOP(sopId);
      }
      if (
        data.additionalData?.entityIds &&
        data.additionalData?.entityIds.length > 0
      ) {
        const entityIds = data.additionalData?.entityIds.map(
          (entity: any) => entity
        );
        setSelectedEntities(entityIds);
      }

      setIsDataLoaded(true);
    } else if (!isEditMode && !isDataLoaded) {
      setIsDataLoaded(true);
    }
  }, [isEditMode, scheduleData, isDataLoaded]);

  useEffect(() => {
    if (assetData) {
      const totalItems = assetData.count || 0;
      const totalPages = Math.ceil(totalItems / pageSize);
      const startItem = (currentPage - 1) * pageSize + 1;
      const endItem = Math.min(currentPage * pageSize, totalItems);

      setPagination({
        currentPage: currentPage,
        totalPages,
        totalItems,
        itemsPerPage: pageSize,
        startItem: totalItems > 0 ? startItem : 0,
        endItem: totalItems > 0 ? endItem : 0,
      });
    }
  }, [assetData, currentPage, pageSize]);

  useEffect(() => {
    if (sopData) {
      const totalItems = sopData.count || 0;
      const totalPages = Math.ceil(totalItems / pageSize);
      const startItem = (sopCurrentPage - 1) * pageSize + 1;
      const endItem = Math.min(sopCurrentPage * pageSize, totalItems);

      setSopPagination({
        currentPage: sopCurrentPage,
        totalPages,
        totalItems,
        itemsPerPage: pageSize,
        startItem: totalItems > 0 ? startItem : 0,
        endItem: totalItems > 0 ? endItem : 0,
      });
    }
  }, [sopData, sopCurrentPage, pageSize]);

  useEffect(() => {
    const entityData = getEntityData();
    if (entityData) {
      const totalItems = entityData.length || 0;
      const totalPages = Math.ceil(totalItems / pageSize);
      const startItem = (entityCurrentPage - 1) * pageSize + 1;
      const endItem = Math.min(entityCurrentPage * pageSize, totalItems);

      setEntityPagination({
        currentPage: entityCurrentPage,
        totalPages,
        totalItems,
        itemsPerPage: pageSize,
        startItem: totalItems > 0 ? startItem : 0,
        endItem: totalItems > 0 ? endItem : 0,
      });
    }
  }, [
    assetData,
    facilityData,
    networkData,
    systemData,
    entityCurrentPage,
    pageSize,
    selectedSOP,
  ]);

  // Memoized options
  const scheduleTypeOptions = useMemo(
    () =>
      scheduleTypeData?.map((choice) => ({
        value: choice.key,
        label: choice.value,
      })),
    [scheduleTypeData]
  );

  const priorityOptions = useMemo(
    () =>
      priorityData?.map((choice) => ({
        value: choice.key,
        label: choice.value,
      })),
    [priorityData]
  );

  const frequencyTypeOptions = useMemo(
    () =>
      frequencyTypeData?.map((choice) => ({
        value: choice.key,
        label: choice.value,
      })),
    [frequencyTypeData]
  );

  const recurrenceOptions = useMemo(
    () =>
      recurrenceData?.map((choice) => ({
        value: choice.key,
        label: choice.value,
      })),
    [recurrenceData]
  );

  const usageTypeOptions = useMemo(
    () =>
      usageTypeData?.map((choice) => ({
        value: choice.key,
        label: choice.value,
      })),
    [usageTypeData]
  );

  // Helper functions
  const getServiceAssociation = () => {
    const sop = sopData?.results.find((s) => s.id === selectedSOP);
    return sop?.sopAssociationTypeDisplay || "Asset";
  };

  const getEntityData = () => {
    const serviceAssociation = getServiceAssociation();
    switch (serviceAssociation) {
      case "System":
        return systemData?.results;
      case "Facility":
        return facilityData?.results;
      case "Network":
        return networkData?.results;
      default:
        return assetData?.results;
    }
  };

  const handleSopPageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      sopPage: page.toString(),
    };
    setSearchParams(newParams);
  };

  const handleEntityPageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      entityPage: page.toString(),
    };
    setSearchParams(newParams);
  };

  // Search handlers
  const handleSearch = (searchTerm: string, paramName: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (searchTerm.trim()) {
      newParams[paramName] = searchTerm.trim();
    } else {
      delete newParams[paramName];
    }

    // Reset to first page when searching
    if (paramName === "sopQuery") {
      newParams.sopPage = "1";
    } else if (paramName === "entityQuery") {
      newParams.entityPage = "1";
    } else {
      newParams.page = "1";
    }

    setSearchParams(newParams);
  };

  // Event handlers
  const handleEntitySelect = (entityId: string) => {
    setSelectedEntities((prev) =>
      prev.includes(entityId)
        ? prev.filter((id) => id !== entityId)
        : [...prev, entityId]
    );
  };

  const handleSelectAll = () => {
    const entityData = getEntityData() || [];
    if (selectedEntities.length === entityData.length) {
      // Unselect all
      setSelectedEntities([]);
    } else {
      // Select all
      setSelectedEntities(entityData.map((entity) => entity.id));
    }
  };

  const isAllSelected = () => {
    const entityData = getEntityData() || [];
    return (
      selectedEntities.length === entityData.length && entityData.length > 0
    );
  };

  const handleSOPSelect = (sopId: number) => {
    setSelectedSOP(sopId);
    setSelectedEntities([]);
  };

  const buildTimeBased = () => {
    const payload: any = {
      timeRecurrence: parseInt(formData.recurrence),
      startDate: formData.startDate,
      preferredTime: formData.timeOfDay || null,
      endDate: formData.endDate || null,
    };

    switch (formData.recurrence) {
      case "1": // Daily
        // No additional fields needed for daily
        break;

      case "2": // Weekly
        payload.days_of_week = formData.weeklyDays || [];
        break;

      case "3": // Monthly
        if (formData.monthlyType === "specific_date") {
          payload.dayOfMonth = Number(formData.dateOfMonth);
          payload.monthlyScheduleType = "specific_date";
        } else {
          payload.monthlyWeek = Number(formData.monthlyWeek);
          payload.monthlyDay = Number(formData.monthlyDay);
          payload.monthlyScheduleType = "day_of_week";
        }
        break;

      case "4": // Quarterly
        payload.startingMonth = Number(formData.quarterlyMonth); // Add this line
        payload.dayOfMonth = Number(formData.quarterlyDay); // Add this line
        break;

      case "5": // Annual
        payload.month = Number(formData.annualMonth); // Changed from annualMonth
        payload.dayOfMonth = Number(formData.annualDay); // Changed from annualDay
        break;

      default:
        break;
    }

    return payload;
  };

  const buildUsageBased = () => {
    return {
      usageType: formData.usageType,
      usageInterval: Number(formData.usageInterval),
      resetAfterMaintenance: true,
    };
  };

  const buildEventBased = () => {
    const eventTriggerConfig: any = {};

    if (formData.eventRules && formData.eventRules.length > 0) {
      formData.eventRules.forEach((rule: any) => {
        if (rule.triggerSource === "service-order") {
          eventTriggerConfig["SERVICE_ORDERS"] = {
            trigger_source: "SERVICE_ORDERS",
            conditions: [
              {
                sop_id: rule.sops, // This should be the SOP ID from dropdown
                reading: rule.readingType, // Changed from rule.sensorType
                operator:
                  rule.operator === "greater-than"
                    ? "gt"
                    : rule.operator === "less-than"
                    ? "lt"
                    : rule.operator === "equal-to"
                    ? "eq"
                    : "gt",
                threshold: Number(rule.value),
                persist_duration: rule.persistCondition // Fixed field name
                  ? Number(rule.persistDuration)
                  : null,
              },
            ],
            operation_type: "AND", // Fixed: underscore not camelCase
          };
        } else if (rule.triggerSource === "calculated") {
          eventTriggerConfig["CALCULATED_ASSET_VALUE"] = {
            trigger_source: "CALCULATED_ASSET_VALUE",
            conditions: [
              {
                metric: rule.metric?.replace("-", "_")?.toUpperCase(),
                operator:
                  rule.operator === "drop-below"
                    ? "lt"
                    : rule.operator === "rises-above"
                    ? "gt"
                    : rule.operator === "is-equal-to"
                    ? "eq"
                    : "lt",
                threshold: Number(rule.value),
              },
            ],
            operation_type: "AND", // Fixed: underscore not camelCase
          };
        }
      });
    }

    // Only return event_trigger_config if it has actual triggers
    if (Object.keys(eventTriggerConfig).length === 0) {
      toast({
        title: "Error",
        description:
          "At least one event rule must be configured for event-based schedules",
      });
    }

    return {
      event_config: eventTriggerConfig, // Fixed: underscore not camelCase
    };
  };

  const getEntityFieldName = (serviceAssociation: string) => {
    switch (serviceAssociation) {
      case "System":
        return "system_ids";
      case "Facility":
        return "facility_ids";
      case "Network":
        return "network_ids";
      case "Asset":
      default:
        return "asset_ids";
    }
  };

  const handleSave = async () => {
    // Now clear any existing errors
    form.clearErrors();

    // Manual validation - check required fields based on frequency type
    const validationErrors: { [key: string]: string } = {};

    // Always required fields
    if (!formData.scheduleName) {
      validationErrors.scheduleName = "Schedule Name is required";
    }

    if (!formData.scheduleType) {
      validationErrors.scheduleType = "Schedule Type is required";
    }

    if (!formData.frequencyType) {
      validationErrors.frequencyType = "Frequency Type is required";
    }

    if (!selectedSOP) {
      validationErrors.selectedSOP = "SOP selection is required";
    }

    if (selectedEntities.length === 0) {
      validationErrors.selectedEntities =
        "At least one entity must be selected";
    }

    // Frequency-specific validations
    if (formData.frequencyType === "0") {
      // Time-based validations
      if (!formData.recurrence) {
        validationErrors.recurrence =
          "Recurrence is required for time-based schedules";
      }

      if (!formData.startDate) {
        validationErrors.startDate =
          "Start date is required for time-based schedules";
      }

      // Additional validations based on recurrence type
      if (
        formData.recurrence === "2" &&
        (!formData.weeklyDays || formData.weeklyDays.length === 0)
      ) {
        validationErrors.weeklyDays =
          "At least one day must be selected for weekly schedules";
      }

      if (formData.recurrence === "3") {
        if (formData.monthlyType === "specific_date" && !formData.dateOfMonth) {
          validationErrors.dateOfMonth = "Date of month is required";
        }
        if (
          formData.monthlyType === "day_of_week" &&
          (!formData.monthlyWeek || !formData.monthlyDay)
        ) {
          validationErrors.monthlyWeek = "Week and day selection is required";
          validationErrors.monthlyDay = "Week and day selection is required";
        }
      }

      if (
        formData.recurrence === "4" &&
        (!formData.quarterlyMonth || !formData.quarterlyDay)
      ) {
        validationErrors.quarterlyMonth = "Quarterly month is required";
        validationErrors.quarterlyDay = "Quarterly day is required";
      }

      if (
        formData.recurrence === "5" &&
        (!formData.annualMonth || !formData.annualDay)
      ) {
        validationErrors.annualMonth = "Annual month is required";
        validationErrors.annualDay = "Annual day is required";
      }
    } else if (formData.frequencyType === "1") {
      // Usage-based validations
      if (!formData.usageType) {
        validationErrors.usageType =
          "Usage type is required for usage-based schedules";
      }

      if (!formData.usageInterval) {
        validationErrors.usageInterval =
          "Usage interval is required for usage-based schedules";
      }
    } else if (formData.frequencyType === "2") {
      // Event-based validations
      if (!formData.eventRules || formData.eventRules.length === 0) {
        validationErrors.eventRules =
          "At least one event rule is required for event-based schedules";
      } else {
        // Validate each event rule
        formData.eventRules.forEach((rule: any, index: number) => {
          if (!rule.triggerSource) {
            validationErrors[`eventRules.${index}.triggerSource`] =
              "Trigger source is required";
          }

          if (rule.triggerSource === "service-order") {
            if (!rule.sops) {
              validationErrors[`eventRules.${index}.sops`] =
                "SOP selection is required";
            }
            if (!rule.readingType) {
              validationErrors[`eventRules.${index}.readingType`] =
                "Reading type is required";
            }
            if (!rule.operator) {
              validationErrors[`eventRules.${index}.operator`] =
                "Operator is required";
            }
            if (!rule.value) {
              validationErrors[`eventRules.${index}.value`] =
                "Value is required";
            }
          }

          if (rule.triggerSource === "calculated") {
            if (!rule.metric) {
              validationErrors[`eventRules.${index}.metric`] =
                "Metric is required";
            }
            if (!rule.operator) {
              validationErrors[`eventRules.${index}.operator`] =
                "Operator is required";
            }
            if (!rule.value) {
              validationErrors[`eventRules.${index}.value`] =
                "Value is required";
            }
          }
        });
      }
    }

    // If there are validation errors, set them and stop
    if (Object.keys(validationErrors).length > 0) {
      // Set errors on the form
      Object.entries(validationErrors).forEach(([fieldName, message]) => {
        form.setError(fieldName, { message });
      });
      return;
    }

    // Rest of your existing save logic...
    const serviceAssociation = sopData?.results?.find(
      (sop) => sop.id === selectedSOP
    )?.sopAssociationTypeDisplay;

    try {
      const basePayload = {
        ...(isEditMode && { id: id }),
        schedule_name: formData.scheduleName,
        schedule_type: Number(formData.scheduleType),
        task_description: formData.taskDescription,
        frequency_type: Number(formData.frequencyType),
        entities: {
          entity_type:
            serviceAssociation === "Asset"
              ? 1
              : serviceAssociation === "System"
              ? 2
              : serviceAssociation === "Facility"
              ? 0
              : 5, // Network
          entity_ids: selectedEntities.map((id) => Number(id)),
        },
        sop_id: selectedSOP,
        default_priority: Number(formData.priority),
        estimated_duration: Number(formData.estimatedDuration),
        auto_generate_service_orders: formData.autoGenerate,
        generate_days_before: Number(formData.generateDays),
        remote_utility_id: remoteUtilityId,
        utility_service_code: "WATER",
        dynamic_asset_inclusion: formData.dynamicSchedule,
      };

      let frequencyConfig = {};

      switch (formData.frequencyType) {
        case "0": // Time-based
          frequencyConfig = { frequency_config: buildTimeBased() };
          break;
        case "1": // Usage-based
          frequencyConfig = { value_config: buildUsageBased() };
          break;
        case "2": // Event-based
          frequencyConfig = buildEventBased();
          break;
        default:
          frequencyConfig = {};
      }

      const finalPayload = {
        ...basePayload,
        ...frequencyConfig,
      };

      if (isEditMode) {
        await updateSchedule.mutateAsync(finalPayload);
      } else {
        await createSchedule.mutateAsync(finalPayload);
      }

      navigate("/ax/service-order/schedule");
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    navigate("/ax/service-order/schedule");
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    if (fieldName === "frequencyType") {
      setFormData((prev) => ({
        ...prev,
        [fieldName]: value,
        recurrence: "",
        startDate: "",
        endDate: "",
        timeOfDay: "",
        weeklyDays: [],
        monthlyType: "specific_date",
        dateOfMonth: "",
        monthlyWeek: "",
        monthlyDay: "",
        quarterlyMonth: "",
        quarterlyDay: "",
        annualMonth: "",
        annualDay: "",
        usageType: "",
        usageInterval: "",
        eventRules: [],
      }));
    }

    if (fieldName === "monthlyType") {
      setFormData((prev) => ({
        ...prev,
        [fieldName]: value,
        dateOfMonth: "",
        monthlyWeek: "",
        monthlyDay: "",
      }));
    }

    if (fieldName === "recurrence") {
      setFormData((prev) => ({
        ...prev,
        [fieldName]: value,
        weeklyDays: [],
        monthlyType: "specific_date",
        dateOfMonth: "",
        monthlyWeek: "",
        monthlyDay: "",
        quarterlyMonth: "",
        quarterlyDay: "",
        annualMonth: "",
        annualDay: "",
        timeOfDay: "",
        endDate: "",
      }));
    }
  };

  // Filter handlers - following CreateServiceOrder pattern
  const handleSOPApplyFilters = useCallback(
    (newFilters: any) => {
      const params = new URLSearchParams(searchParams);
      params.delete("serviceAssociation");
      params.delete("operationType");
      params.delete("sopPage");

      // Only adds non-empty filters
      if (newFilters.serviceAssociation) {
        params.set("serviceAssociation", newFilters.serviceAssociation);
      }
      if (newFilters.operationType) {
        params.set("operationType", newFilters.operationType);
      }

      params.set("sopPage", "1");
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleSOPResetFilters = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete("serviceAssociation");
    params.delete("operationType");
    params.delete("sopPage");
    params.delete("sopQuery");
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const handleEntityApplyFilters = useCallback(
    (newFilters: any) => {
      const params = new URLSearchParams(searchParams);
      params.delete("area");
      params.delete("type");
      params.delete("entityPage");
      params.delete("system");
      params.delete("facility");
      params.delete("network");

      if (newFilters.area) {
        params.set("area", newFilters.area);
      }
      if (newFilters.type) {
        params.set("type", newFilters.type);
      }
      if (newFilters.facility) {
        params.set("facility", newFilters.facility);
      }
      if (newFilters.network) {
        params.set("network", newFilters.network);
      }
      if (newFilters.system) {
        params.set("system", newFilters.system);
      }

      params.set("entityPage", "1");
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleEntityResetFilters = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete("area");
    params.delete("system");
    params.delete("network");
    params.delete("facility");
    params.delete("type");
    params.delete("entityPage");
    params.delete("entityQuery");
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const hasActiveSOPFilters =
    searchParams.get("sopQuery") ||
    searchParams.get("serviceAssociation") ||
    searchParams.get("operationType");

  const hasActiveEntityFilters =
    searchParams.get("entityQuery") ||
    searchParams.get("area") ||
    searchParams.get("type") ||
    searchParams.get("system") ||
    searchParams.get("facility") ||
    searchParams.get("network");

  const sopColumns: TableColumn<SOP>[] = [
    {
      key: "select",
      header: "Select",
      width: "w-16",
      type: "custom",
      sortable: false,
      render: (_, row) => (
        <input
          type="radio"
          name="sopSelection"
          checked={selectedSOP === (row.id as any)}
          onChange={() => handleSOPSelect(row.id as any)}
          className="h-4 w-4 text-blue-600"
        />
      ),
    },
    {
      key: "serviceNo",
      header: "SOP ID",
      width: "w-32",
      type: "custom",
      render: (value: string) => <span className="font-medium">{value}</span>,
    },
    {
      key: "name",
      header: "SOP Name",
      width: "w-64",
    },
    {
      key: "sopAssociationTypeDisplay",
      header: "Service Association",
      width: "w-32",
      type: "badge",
      badgeConfig: {
        getVariant: () => "default",
        getClassName: (value: string) => {
          switch (value) {
            case "Assets":
              return "bg-blue-100 text-blue-800 border-blue-200";
            case "Systems":
              return "bg-green-100 text-green-800 border-green-200";
            case "Facilities":
              return "bg-purple-100 text-purple-800 border-purple-200";
            case "Networks":
              return "bg-orange-100 text-orange-800 border-orange-200";
            default:
              return "bg-gray-100 text-gray-800 border-gray-200";
          }
        },
      },
    },
    {
      key: "operationTypeDisplay",
      header: "Operation Type",
      width: "w-40",
      type: "badge",
      badgeConfig: {
        getVariant: () => "default",
        getClassName: () => "bg-indigo-100 text-indigo-800 border-indigo-200",
      },
    },
    {
      key: "taskCount",
      header: "Tasks",
      width: "w-24",
      type: "custom",
      render: (value: string) => (
        <span className="text-sm text-gray-600">
          {value || 0} {Number(value) > 2 ? "tasks" : "task"}
        </span>
      ),
    },
    {
      key: "estTotalCost",
      header: "Total Cost",
      width: "w-24",
      type: "custom",
      render: (value: string) => (
        <span className="font-medium text-green-600">
          {currency} {value}
        </span>
      ),
    },
  ];

  // Entity Table Columns (Dynamic based on service association)
  const getEntityColumns = (): TableColumn<Entity>[] => {
    const serviceAssociation = getServiceAssociation();

    switch (serviceAssociation) {
      case "System":
        return [
          {
            key: "select",
            header: (
              <Checkbox
                checked={isAllSelected()}
                onCheckedChange={handleSelectAll}
              />
            ),
            width: "w-5",
            type: "custom",
            sortable: false,
            render: (_, row) => (
              <div className="flex items-center justify-center">
                <Checkbox
                  checked={selectedEntities.includes(row.id)}
                  onCheckedChange={() => handleEntitySelect(row.id)}
                />
              </div>
            ),
          },
          {
            key: "systemNo",
            header: `System ID`,
            width: "w-32",
            type: "custom",
            render: (value: string) => (
              <span className="font-medium">{value}</span>
            ),
          },
          {
            key: "systemName",
            header: `System Name`,
            width: "w-64",
          },
          {
            key: "systemTypeDisplay",
            header: "Type",
            width: "w-32",
          },
          {
            key: "statusDisplay",
            header: "Status",
            width: "w-32",
            type: "badge",
            badgeConfig: {
              getVariant: () => "default",
              getClassName: (value: string) =>
                value === "Operational"
                  ? "bg-gray-500 text-white"
                  : "bg-orange-100 text-orange-800 border-orange-200",
            },
          },
          {
            key: "criticalityLevelDisplay",
            header: "Criticality",
            width: "w-32",
            type: "badge",
            badgeConfig: {
              getVariant: () => "default",
              getClassName: (value: string) => {
                switch (value) {
                  case "Very High":
                    return "bg-red-500 text-white";
                  case "High":
                    return "bg-red-400 text-white";
                  default:
                    return "bg-blue-100 text-blue-800 border-blue-200";
                }
              },
            },
          },
          { key: "facilityName", header: "Facility", width: "w-32" },
          { key: "location", header: "Location", width: "w-48" },
        ];

      case "Facility":
        return [
          {
            key: "select",
            header: (
              <Checkbox
                checked={isAllSelected()}
                onCheckedChange={handleSelectAll}
              />
            ),
            width: "w-5",
            type: "custom",
            sortable: false,
            render: (_, row) => (
              <div className="flex items-center justify-center">
                <Checkbox
                  checked={selectedEntities.includes(row.id)}
                  onCheckedChange={() => handleEntitySelect(row.id)}
                />
              </div>
            ),
          },
          {
            key: "facilityNo",
            header: `Facility ID`,
            width: "w-32",
            type: "custom",
            render: (value: string) => (
              <span className="font-medium">{value}</span>
            ),
          },
          {
            key: "facilityName",
            header: `Facility Name`,
            width: "w-64",
          },
          {
            key: "facilityTypeDisplay",
            header: "Type",
            width: "w-32",
          },
          {
            key: "serviceZoneDisplay",
            header: "Zone / Division",
            width: "w-32",
          },
          { key: "systemsCount", header: "Systems", width: "w-24" },
          { key: "assetsCount", header: "Assets", width: "w-24" },
          {
            key: "conditionScore",
            header: "Condition Score",
            width: "w-32",
            type: "custom",
            render: (value: number) => (
              <div className="flex items-center gap-2">
                <span>{value}</span>
                <Badge
                  variant="outline"
                  className={
                    value >= 80
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }
                >
                  {value >= 80 ? "Good" : "Fair"}
                </Badge>
              </div>
            ),
          },
          {
            key: "overallDesignCapacity",
            header: "Design / Current Capacity",
            width: "w-48",
          },
        ];

      case "Network":
        return [
          {
            key: "select",
            header: (
              <Checkbox
                checked={isAllSelected()}
                onCheckedChange={handleSelectAll}
              />
            ),
            width: "w-5",
            type: "custom",
            sortable: false,
            render: (_, row) => (
              <div className="flex items-center justify-center">
                <Checkbox
                  checked={selectedEntities.includes(row.id)}
                  onCheckedChange={() => handleEntitySelect(row.id)}
                />
              </div>
            ),
          },
          {
            key: "networkNo",
            header: `Network ID`,
            width: "w-32",
            type: "custom",
            render: (value: string) => (
              <span className="font-medium">{value}</span>
            ),
          },
          {
            key: "networkName",
            header: `Network Name`,
            width: "w-64",
          },
          {
            key: "networkType",
            header: "Type",
            width: "w-32",
          },
          {
            key: "operationalStatusDisplay",
            header: "Operating Status",
            width: "w-32",
          },
          {
            key: "designCapacity",
            header: "Current/Design Capacity",
            width: "w-24",
          },
          {
            key: "customerConnections",
            header: "Customer Connections",
            width: "w-24",
          },
          { key: "zonesDisplay", header: "Zone / Division", width: "w-32" },
        ];

      case "Asset":
        return [
          {
            key: "select",
            header: (
              <Checkbox
                checked={isAllSelected()}
                onCheckedChange={handleSelectAll}
              />
            ),
            width: "w-5",
            type: "custom",
            sortable: false,
            render: (_, row) => (
              <div className="flex items-center justify-center">
                <Checkbox
                  checked={selectedEntities.includes(row.id)}
                  onCheckedChange={() => handleEntitySelect(row.id)}
                />
              </div>
            ),
          },
          { key: "assetNo", header: "Asset ID", width: "w-32" },
          { key: "assetName", header: "Asset Name", width: "w-64" },
          { key: "assetTypeDisplay", header: "Type", width: "w-32" },
          { key: "location", header: "Location", width: "w-48" },
          { key: "facilityName", header: "Facility", width: "w-32" },
          { key: "systemName", header: "System", width: "w-32" },
          {
            key: "conditionScores",
            header: "Condition",
            width: "w-32",
            type: "custom",
            render: (value: number) => (
              <div className="flex items-center gap-2">
                <span className="font-medium">{value}</span>
                <Badge
                  variant="secondary"
                  className={getConditionLabel(value).color}
                >
                  {getConditionLabel(value).label}
                </Badge>
              </div>
            ),
          },
        ];
      default:
        return [];
    }
  };

  const fields: FormField[] = [
    {
      name: "scheduleName",
      label: "Schedule Name",
      type: "text",
      required: false,
      placeholder: "e.g., Monthly Pump Inspection - Station 3",
      showHelperTooltip: true,
      groupColorNeeded: false,
      group: "Schedule Identification & Details",
      groupHelperText:
        "Define the basic schedule information, type, and task details",
      tooltipText:
        "Provide a clear, descriptive name for the schedule (e.g., Monthly Pump Inspection – Station A). Helps in quickly identifying and managing schedules.",
      helperText: "Provide a clear, concise name for this recurring task",
    },
    {
      name: "scheduleType",
      label: "Schedule Type",
      type: "select",
      required: false,
      placeholder: "Select schedule type",
      helperText: "Choose the type of work to be performed",
      showHelperTooltip: true,
      groupColorNeeded: false,
      group: "Schedule Identification & Details",
      groupHelperText:
        "Define the basic schedule information, type, and task details",
      tooltipText:
        "Choose the type of work (e.g., Preventive Maintenance, Inspection, Corrective). Determines the overall category of tasks for this schedule.",
      options: scheduleTypeOptions,
    },
    {
      name: "taskDescription",
      label: "Task Description / Instructions",
      type: "textarea",
      required: false,
      placeholder: "What needs to be done during this maintenance/inspection?",
      helperText: "Describe the work to be performed",
      showHelperTooltip: true,
      groupColorNeeded: false,
      group: "Schedule Identification & Details",
      groupHelperText:
        "Define the basic schedule information, type, and task details",
      tooltipText:
        "Add specific details or instructions describing what needs to be done during this scheduled task. Provides clarity for technicians.",
      rows: 4,
      className: "col-span-2",
    },
    {
      name: "priority",
      label: "Default Priority",
      type: "select",
      required: false,
      helperText: "Set the default priority level",
      showHelperTooltip: true,
      groupColorNeeded: false,
      group: "Schedule Identification & Details",
      groupHelperText:
        "Define the basic schedule information, type, and task details",
      tooltipText:
        "Defines the urgency of the recurring schedule (Low, Medium, High, Critical). Affects planning and task assignment.",
      options: priorityOptions,
      className: "col-span-1",
    },
    {
      name: "estimatedDuration",
      label: "Estimated Duration (Hours)",
      type: "decimal",
      required: false,
      placeholder: "e.g., 2",
      helperText: "Estimated time to complete the task",
      showHelperTooltip: true,
      groupColorNeeded: false,
      group: "Schedule Identification & Details",
      groupHelperText:
        "Define the basic schedule information, type, and task details",
      tooltipText:
        "Approximate time required to complete the scheduled task. Useful for workforce planning and workload balancing.",
      step: 0.5,
      className: "col-span-1",
    },
    // {
    //   name: "dynamicSchedule",
    //   label: "",
    //   type: "switch",
    //   className: "col-span-full",
    //   groupColorNeeded: false,
    //   required: false,
    //   group: "Schedule Identification & Details",
    //   groupHelperText:
    //     "Define the basic schedule information, type, and task details",
    //   customRender: ({ field }: any) => (
    //     <div className="border-t pt-4">
    //       <div className="flex items-center justify-between">
    //         <div className="space-y-1">
    //           <div className="flex items-center gap-2">
    //             <Label className="text-sm font-semibold">
    //               Dynamic Schedule
    //             </Label>
    //             <TooltipProvider delayDuration={200}>
    //               <Tooltip>
    //                 <TooltipTrigger asChild>
    //                   <HelpCircle className="h-4 w-4 text-muted-foreground cursor-pointer" />
    //                 </TooltipTrigger>
    //                 <TooltipContent>
    //                   <p className="max-w-xs">
    //                     When enabled, new assets that match the criteria of
    //                     selected assets are automatically added to the schedule.
    //                   </p>
    //                 </TooltipContent>
    //               </Tooltip>
    //             </TooltipProvider>
    //           </div>
    //           <p className="text-xs text-gray-500">
    //             Automatically include new assets that match the criteria of
    //             selected assets (same asset type, location, or system)
    //           </p>
    //         </div>
    //         <Switch
    //           checked={formData.dynamicSchedule}
    //           onCheckedChange={(value) =>
    //             handleFieldChange("dynamicSchedule", value)
    //           }
    //         />
    //       </div>
    //       {formData.dynamicSchedule && (
    //         <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
    //           <p className="text-sm text-blue-800">
    //             <strong>Dynamic Schedule Enabled:</strong> When new assets are
    //             added to the system that match the asset type, location, or
    //             system of currently selected assets, they will automatically be
    //             included in this schedule.
    //           </p>
    //         </div>
    //       )}
    //     </div>
    //   ),
    // },
    // {/* Removed for now will need after */}

    // Section Header - SOP Selection
    {
      name: "sopSelection",
      label: "",
      type: "custom",
      className: "col-span-full",
      groupColorNeeded: false,
      group: "SOP Selection",
      groupHelperText:
        "Select the standard operating procedure that will be applied to this schedule",
      customRender: ({ field }: any) => {
        return (
          // Removed nested divs and fixed padding
          <div className="space-y-4">
            <GenericTable
              data={sopData?.results}
              columns={sopColumns}
              searchPlaceholder="Search by SOP ID, SOP Name..."
              searchParamName="sopQuery"
              searchDebounceMs={300}
              onSearch={handleSearch}
              rowKey="id"
              pagination={sopPagination}
              onPageChange={handleSopPageChange}
              emptyMessage="No SOPs found"
              filterPanel={
                <SOPFilterPanel
                  currentFilters={sopQueryParams}
                  onApplyFilters={handleSOPApplyFilters}
                  onResetFilters={handleSOPResetFilters}
                />
              }
              filters={
                hasActiveSOPFilters ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      Filters active
                    </span>
                  </div>
                ) : null
              }
            />

            {selectedSOP && (
              <p className="text-sm text-gray-600">
                1 SOP selected:{" "}
                {sopData?.results?.find((sop) => sop.id === selectedSOP)?.name}
              </p>
            )}
          </div>
        );
      },
    },

    // Section Header - Entity Selection
    {
      name: "entitySelection",
      label: "",
      type: "custom",
      className: "col-span-full",
      showWhen: () => !!selectedSOP,
      required: false,
      groupColorNeeded: false,
      group: `${
        sopData?.results?.find((sop) => sop.id === selectedSOP)
          ?.sopAssociationTypeDisplay || "Entity"
      } Selection`,
      groupHelperText:
        "Select the entities that will be included in this schedule",
      customRender: ({ field }: any) => {
        const serviceAssociation = getServiceAssociation();

        return (
          // Removed nested divs and border wrapper
          <div className="space-y-4">
            <GenericTable
              data={getEntityData()}
              columns={getEntityColumns()}
              loading={false}
              searchPlaceholder={`Search by ID, Name...`}
              searchParamName="entityQuery"
              searchDebounceMs={300}
              onSearch={handleSearch}
              rowKey="id"
              pagination={entityPagination}
              onPageChange={handleEntityPageChange}
              filterPanel={
                <EntityFilterPanel
                  currentFilters={entityQueryParams}
                  onApplyFilters={handleEntityApplyFilters}
                  onResetFilters={handleEntityResetFilters}
                  serviceAssociation={serviceAssociation}
                />
              }
              emptyMessage={`No ${serviceAssociation.toLowerCase()} found`}
              filters={
                hasActiveEntityFilters ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      Filters active
                    </span>
                  </div>
                ) : null
              }
            />

            {selectedEntities.length > 0 && (
              <p className="text-sm text-gray-600">
                {selectedEntities.length} {serviceAssociation.toLowerCase()}{" "}
                selected
              </p>
            )}
          </div>
        );
      },
    },

    // === MAIN FREQUENCY TYPE ===
    {
      name: "frequencyType",
      label: "Frequency Type",
      type: "select",
      required: false,
      placeholder: "Select frequency type",
      helperText: "Choose how the schedule should be triggered",
      showHelperTooltip: true,
      className: "col-span-3",
      groupColorNeeded: false,
      group: "Schedule Frequency & Timing",
      groupHelperText: "Define when and how often the schedule should trigger",
      tooltipText: (
        <ul>
          <li>Defines how often the schedule should trigger. Options:</li>
          <li>
            <strong>Time-Based:</strong> Triggered by calendar intervals (daily,
            weekly, monthly).
          </li>
          <li>
            <strong>Usage-Based:</strong> Triggered after specific usage
            thresholds (e.g., every 500 run hours, every 10,000 gallons pumped).
          </li>
          <li>
            <strong>Event-Based:</strong> Triggered automatically when a
            condition, Service Order, or calculated metric meets defined
            criteria.
          </li>
        </ul>
      ),
      options: frequencyTypeOptions,
    },

    // === TIME-BASED FIELDS ===
    {
      name: "recurrence",
      label: "Recurrence",
      type: "select",
      required: false,
      placeholder: "Select frequency",
      showHelperTooltip: true,
      className: "col-span-3",
      groupColorNeeded: false,
      group: "Schedule Frequency & Timing",
      groupHelperText: "Define when and how often the schedule should trigger",
      tooltipText:
        "Select how often the schedule repeats (Daily, Weekly, Monthly, Quarterly, Annually).",
      showWhen: (formValues) => formValues.frequencyType === "0",
      options: recurrenceOptions,
    },

    {
      name: "startDate",
      label: "Start Date",
      type: "datepicker",
      required: false,
      showHelperTooltip: true,
      className: "col-span-3",
      groupColorNeeded: false,
      group: "Schedule Frequency & Timing",
      groupHelperText: "Define when and how often the schedule should trigger",
      tooltipText: "Choose the date when the schedule will begin.",
      showWhen: (formValues) => formValues.frequencyType === "0",
    },

    // Weekly - Day of Week Selection
    {
      name: "weeklyDays",
      label: "Day of Week",
      type: "multi-select",
      showWhen: (formValues) =>
        formValues.frequencyType === "0" && formValues.recurrence === "2",
      options: [
        { value: 0, label: "Sunday" },
        { value: 1, label: "Monday" },
        { value: 2, label: "Tuesday" },
        { value: 3, label: "Wednesday" },
        { value: 4, label: "Thursday" },
        { value: 5, label: "Friday" },
        { value: 6, label: "Saturday" },
      ],
      helperText: "Select which days of the week to schedule",
      className: "col-span-3",
      groupColorNeeded: false,
      group: "Schedule Frequency & Timing",
      groupHelperText: "Define when and how often the schedule should trigger",
    },

    // Monthly - Schedule Type
    {
      name: "monthlyType",
      label: "Monthly Schedule Type",
      type: "radio",
      showWhen: (formValues) =>
        formValues.frequencyType === "0" && formValues.recurrence === "3",
      options: [
        { value: "specific_date", label: "Specific Date of Month" },
        { value: "day_of_week", label: "Specific Day of Week" },
      ],
      defaultValue: "specific_date",
      className: "col-span-3",
      groupColorNeeded: false,
      group: "Schedule Frequency & Timing",
      groupHelperText: "Define when and how often the schedule should trigger",
    },

    {
      name: "dateOfMonth",
      label: "Date of Month",
      type: "select",
      showWhen: (formValues) =>
        formValues.frequencyType === "0" &&
        formValues.recurrence === "3" &&
        formValues.monthlyType === "specific_date",
      options: Array.from({ length: 28 }, (_, i) => ({
        value: i + 1,
        label: String(i + 1),
      })),
      className: "col-span-3",
      groupColorNeeded: false,
      group: "Schedule Frequency & Timing",
      groupHelperText: "Define when and how often the schedule should trigger",
    },

    {
      name: "monthlyWeek",
      label: "Week of Month",
      type: "select",
      showWhen: (formValues) =>
        formValues.frequencyType === "0" &&
        formValues.recurrence === "3" &&
        formValues.monthlyType === "day_of_week",
      options: [
        { value: 0, label: "First" },
        { value: 1, label: "Second" },
        { value: 2, label: "Third" },
        { value: 3, label: "Fourth" },
      ],
      className: "col-span-3",
      groupColorNeeded: false,
      group: "Schedule Frequency & Timing",
      groupHelperText: "Define when and how often the schedule should trigger",
    },

    {
      name: "monthlyDay",
      label: "Day of Week",
      type: "select",
      showWhen: (formValues) =>
        formValues.frequencyType === "0" &&
        formValues.recurrence === "3" &&
        formValues.monthlyType === "day_of_week",
      options: [
        { value: 0, label: "Sunday" },
        { value: 1, label: "Monday" },
        { value: 2, label: "Tuesday" },
        { value: 3, label: "Wednesday" },
        { value: 4, label: "Thursday" },
        { value: 5, label: "Friday" },
        { value: 6, label: "Saturday" },
      ],
      className: "col-span-3",
      groupColorNeeded: false,
      group: "Schedule Frequency & Timing",
      groupHelperText: "Define when and how often the schedule should trigger",
    },

    // Quarterly fields
    {
      name: "quarterlyMonth",
      label: "Starting Month",
      type: "select",
      showWhen: (formValues) =>
        formValues.frequencyType === "0" && formValues.recurrence === "4",
      options: [
        { value: 1, label: "January" },
        { value: 2, label: "February" },
        { value: 3, label: "March" },
        { value: 4, label: "April" },
        { value: 5, label: "May" },
        { value: 6, label: "June" },
        { value: 7, label: "July" },
        { value: 8, label: "August" },
        { value: 9, label: "September" },
        { value: 10, label: "October" },
        { value: 11, label: "November" },
        { value: 12, label: "December" },
      ],
      className: "col-span-3",
      groupColorNeeded: false,
      group: "Schedule Frequency & Timing",
      groupHelperText: "Define when and how often the schedule should trigger",
    },

    {
      name: "quarterlyDay",
      label: "Day of Month",
      type: "number",
      min: 1,
      max: 31,
      placeholder: "Day",
      showWhen: (formValues) =>
        formValues.frequencyType === "0" && formValues.recurrence === "4",
      className: "col-span-3",
      groupColorNeeded: false,
      group: "Schedule Frequency & Timing",
      groupHelperText: "Define when and how often the schedule should trigger",
    },

    // Annual fields
    {
      name: "annualMonth",
      label: "Month",
      type: "select",
      showWhen: (formValues) =>
        formValues.frequencyType === "0" && formValues.recurrence === "5",
      options: [
        { value: 1, label: "January" },
        { value: 2, label: "February" },
        { value: 3, label: "March" },
        { value: 4, label: "April" },
        { value: 5, label: "May" },
        { value: 6, label: "June" },
        { value: 7, label: "July" },
        { value: 8, label: "August" },
        { value: 9, label: "September" },
        { value: 10, label: "October" },
        { value: 11, label: "November" },
        { value: 12, label: "December" },
      ],
      className: "col-span-3",
      groupColorNeeded: false,
      group: "Schedule Frequency & Timing",
      groupHelperText: "Define when and how often the schedule should trigger",
    },

    {
      name: "annualDay",
      label: "Day of Month",
      type: "number",
      min: 1,
      max: 31,
      placeholder: "Day",
      showWhen: (formValues) =>
        formValues.frequencyType === "0" && formValues.recurrence === "5",
      className: "col-span-3",
      groupColorNeeded: false,
      group: "Schedule Frequency & Timing",
      groupHelperText: "Define when and how often the schedule should trigger",
    },

    // Time of Day (Common for all time-based frequencies)
    {
      name: "timeOfDay",
      label: "Preferred Time",
      type: "time",
      showWhen: (formValues) =>
        formValues.frequencyType === "0" && formValues.recurrence,
      className: "col-span-3",
      groupColorNeeded: false,
      required: false,
      group: "Schedule Frequency & Timing",
      groupHelperText: "Define when and how often the schedule should trigger",
    },

    {
      name: "endDate",
      label: "End Date (Optional)",
      type: "datepicker",
      showWhen: (formValues) =>
        formValues.frequencyType === "0" && formValues.recurrence,
      className: "col-span-3",
      groupColorNeeded: false,
      group: "Schedule Frequency & Timing",
      groupHelperText: "Define when and how often the schedule should trigger",
    },

    // === USAGE-BASED FIELDS ===
    {
      name: "usageType",
      label: "Usage Type",
      type: "select",
      required: false,
      showHelperTooltip: true,
      tooltipText:
        "Select the usage metric to trigger scheduling (e.g., Run Hours, Gallons Pumped).",
      showWhen: (formValues) => formValues.frequencyType === "1",
      options: usageTypeOptions,
      className: "col-span-3",
      groupColorNeeded: false,
      group: "Schedule Frequency & Timing",
      groupHelperText: "Define when and how often the schedule should trigger",
    },

    {
      name: "usageInterval",
      label: "Usage Interval",
      type: "number",
      required: false,
      showHelperTooltip: true,
      tooltipText:
        "Define the threshold value that triggers scheduling (e.g., every 500 run hours).",
      placeholder: "e.g., 500",
      showWhen: (formValues) => formValues.frequencyType === "1",
      className: "col-span-3",
      groupColorNeeded: false,
      group: "Schedule Frequency & Timing",
      groupHelperText: "Define when and how often the schedule should trigger",
    },

    //Event based
    {
      name: "eventRules",
      label: "Event-Based Rules",
      type: "form-array",
      showWhen: (formValues) => formValues.frequencyType === "2",
      required: false,
      helperText:
        "Build rules that automatically trigger when specific conditions are met",
      className: "col-span-3",
      groupColorNeeded: false,
      group: "Schedule Frequency & Timing",
      groupHelperText: "Define when and how often the schedule should trigger",
      addButtonText: "AND (Add Another Condition)",
      emptyMessage: "No event rules configured",
      itemFields: [
        {
          name: "triggerSource",
          label: "IF the trigger is a...",
          type: "select",
          className: "col-span-full",
          required: false,
          placeholder: "Select trigger source",
          options: [
            { value: "service-order", label: "Service Order" },
            { value: "calculated", label: "Calculated Asset Value" },
          ],
        },

        {
          name: "sops",
          label: "SOP's",
          type: "select",
          showWhen: (formValues) => {
            return formValues?.triggerSource === "service-order";
          },
          options:
            eventSOPData?.results?.map((sop: any) => ({
              value: sop.id,
              label: `${sop.name}`,
            })) || [],
          helperText: "Select Service Order from the list",
          className: "col-span-3",
          groupColorNeeded: false,
          group: "Schedule Frequency & Timing",
          groupHelperText:
            "Define when and how often the schedule should trigger",
          onChange: (value) => {
            setSelectedEventSOP(value);
          },
        },

        // Sensor-specific fields
        {
          name: "readingType",
          label: "Reading",
          type: "select",

          required: false,
          placeholder: "Select sensor",
          className: "col-span-1",
          showWhen: (formValues) => {
            return formValues?.triggerSource === "service-order";
          },
          options: (() => {
            // Collect all unique reading names from all SOPs
            const allReadings = new Set();

            if (eventSOPData?.results) {
              eventSOPData.results.forEach((sop: any) => {
                if (sop.readings && Array.isArray(sop.readings)) {
                  sop.readings.forEach((reading: any) => {
                    if (reading.name) {
                      allReadings.add(reading.name);
                    }
                  });
                }
              });
            }

            // Convert Set to options array
            return Array.from(allReadings).map((readingName: string) => ({
              value: readingName,
              label: readingName,
            }));
          })(),
        },

        // Calculated metric fields
        {
          name: "metric",
          label: "Metric",
          type: "select",
          required: false,
          className: "col-span-1",
          placeholder: "Select metric",
          showWhen: (formValues, fieldPath) => {
            return formValues?.triggerSource === "calculated";
          },
          options: [
            { value: "condition-score", label: "Condition Score" },
            { value: "risk-score", label: "Risk Score" },
          ],
        },
        {
          name: "operator",
          label: "is",
          type: "select",
          required: false,
          className: "col-span-1",
          placeholder: "Select operator",
          showWhen: (formValues, fieldPath) => {
            const triggerSource = formValues?.triggerSource;
            return triggerSource === "service-order";
          },
          options: [
            { value: "greater-than", label: "Greater than" },
            { value: "less-than", label: "Less than" },
            { value: "equal-to", label: "Equal to" },
            { value: "not-equal-to", label: "Not equal to" },
            { value: "between", label: "Between" },
            { value: "not-between", label: "Not between" },
          ],
        },
        {
          name: "operator",
          label: "is",
          type: "select",
          required: false,
          className: "col-span-1",
          placeholder: "Select operator",
          showWhen: (formValues, fieldPath) => {
            const triggerSource = formValues?.triggerSource;
            return triggerSource === "calculated";
          },
          options: [
            { value: "drop-below", label: "Drops Below" },
            { value: "rises-above", label: "Rises Above" },
            { value: "is-equal-to", label: "Is Equal to" },
          ],
        },

        {
          name: "value",
          label: "Value",
          type: "number",
          required: false,
          className: "col-span-1",
          placeholder: "Enter value",
          showWhen: (formValues, fieldPath) => {
            const triggerSource = formValues?.triggerSource;
            return (
              triggerSource === "service-order" ||
              triggerSource === "calculated"
            );
          },
        },
        {
          name: "secondValue",
          label: "Value",
          type: "number",
          required: false,
          className: "col-span-1",
          placeholder: "Enter value",
          showWhen: (formValues) => {
            return (
              ["service-order", "calculated"].includes(
                formValues?.triggerSource
              ) && ["between", "not-between"].includes(formValues?.operator)
            );
          },
        },
      ],
    },
  ];

  // Form configuration
  const config: FormConfig = {
    fields: fields,
    layout: "grid",
    showSubmitButton: true,
    showResetButton: true,
    submitButtonText:
      createSchedule.isPending || updateSchedule.isPending
        ? isEditMode
          ? "Updating..."
          : "Saving..."
        : isEditMode
        ? "Update Schedule"
        : "Save Schedule",
    resetButtonText: "Cancel",
    // spacing: "comfortable",
    classes: FIELD_CLASSES,
  };

  const form = FormService.createForm({
    fields: fields,
    mode: "onSubmit",
    validateOnMount: false,
    defaultValues: formData,
  });

  // Don't render until data is loaded to prevent unnecessary re-renders
  if (!isDataLoaded) {
    return (
      <div className="p-3">
        <div className="space-y-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode ? "Edit Schedule" : "Add New Schedule"}
            </h1>
            <p className="text-gray-600 mt-1">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <ArrowLeft
            className="w-5 h-5 cursor-pointer text-gray-700"
            onClick={() => navigate("/ax/service-order/schedule")}
          />
          <PageHeader
            title={isEditMode ? "Edit Schedule" : "Add New Schedule"}
            subtitle={
              isEditMode
                ? "Update the schedule configuration and settings"
                : "Configure schedule details, frequency, and entity selection"
            }
          />
        </div>

        <DynamicForm
          fields={fields}
          form={form}
          config={config}
          gridColumns={2}
          responsiveColumns={{ sm: 1, md: 2, lg: 2 }}
          className="unified-selection-form"
          onFieldChange={handleFieldChange}
          onSubmit={handleSave}
          onReset={handleCancel}
          loading={createSchedule.isPending || updateSchedule.isPending}
        />
      </div>
    </div>
  );
};

export default AddSchedule;
